// Phase 7 R1 — atomic PO creation via RPC.
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export interface CreatePurchaseOrderLineInput {
  parts_line_id?: string | null;
  ticket_id?: string | null;
  job_id?: string | null;
  description: string;
  vendor_part_number?: string | null;
  quantity_ordered: number;
  unit_cost_ordered?: number | null;
  core_charge_ordered?: number | null;
}

export interface CreatePurchaseOrderInput {
  vendor_name: string;
  expected_at?: string | null;
  notes?: string | null;
  lines: CreatePurchaseOrderLineInput[];
}

export function useCreatePurchaseOrder() {
  const qc = useQueryClient();
  const { activeShop } = useAuth();
  const shopId = activeShop?.id;

  return useMutation({
    mutationFn: async (input: CreatePurchaseOrderInput): Promise<string> => {
      if (!shopId) throw new Error('No active shop');
      if (!input.lines.length) throw new Error('Add at least one line');

      const { data, error } = await (supabase as any).rpc('create_purchase_order', {
        p_shop_id: shopId,
        p_vendor_name: input.vendor_name ?? '',
        p_expected_at: input.expected_at ?? null,
        p_notes: input.notes ?? '',
        p_lines: input.lines.map((l) => ({
          parts_line_id: l.parts_line_id ?? null,
          ticket_id: l.ticket_id ?? null,
          job_id: l.job_id ?? null,
          description: l.description,
          vendor_part_number: l.vendor_part_number ?? null,
          quantity_ordered: l.quantity_ordered,
          unit_cost_ordered: l.unit_cost_ordered ?? null,
          core_charge_ordered: l.core_charge_ordered ?? 0,
        })),
      });

      if (error) throw error;
      return data as string;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['purchase-orders'] });
      // Also invalidate parts queries so any line links refresh.
      qc.invalidateQueries({ queryKey: ['parts-lines'] });
    },
  });
}
